import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useOrganizations from '../models/useOrganizations'
import useNode from '../models/useNode'
import useNetwork from '../models/useNetwork'
import { missingStep, type Asked } from '../utilities/missingObject'
import { HOME } from '../utilities/orgSwitch'

export { HOME }

/**
 * The router state of the navigate that closes a page. The drawer reads it: the page it opens
 * again must not be the page that just closed itself.
 */
export const OBJECT_GONE = { objectGone: true } as const

type Args = {
  /** The id in the path. */
  id: string | undefined
  /** The page has its object. */
  found: boolean
  /** The organization of the last read of the list the object comes from. */
  loadedOrgId: string | null | undefined
  /** Reads the object itself: the id of its organization, false for no such object, null for a failed read. */
  orgOf: (id: string) => Promise<string | false | null>
  /** Where to go when the reader can read no such object: `HOME`. */
  to: string
}

/**
 * For a page or popup whose path names one object that the store does not hold (app#473, app#478):
 * - the object is in ANOTHER organization of the reader: that organization becomes the active
 *   one, and the page draws the object. The reader sees a notice (`OrgSelector`). The app also
 *   remembers it as the organization to open next time, as for a pick in the header.
 * - the reader can read no such object: the page goes to the home view.
 * The rule, and the cases where NOTHING must happen, are in `utilities/missingObject.ts`.
 *
 * ⚠ `replace` on the close: the entry that names the object is the one that cannot be drawn, so
 * Back must not return to it.
 */
export default function useCloseWhenMissing({ id, found, loadedOrgId, orgOf, to }: Args) {
  const navigate = useNavigate()
  const activeOrgId = useOrganizations(s => s.activeOrgId)
  const organizations = useOrganizations(s => s.organizations)
  // The answer belongs to ONE object in ONE organization. A new path or a new organization is a
  // new question.
  const [answer, setAnswer] = useState<{ key: string; asked: Asked }>({ key: '', asked: undefined })
  const key = `${activeOrgId ?? ''}/${id ?? ''}`
  const asked = answer.key === key ? answer.asked : undefined
  const step = id
    ? missingStep(
        found,
        loadedOrgId,
        activeOrgId,
        asked,
        organizations.map(o => o.id)
      )
    : 'wait'

  useEffect(() => {
    if (step === 'close') navigate(to, { replace: true, state: OBJECT_GONE })
    if (step === 'switch' && typeof asked === 'string') useOrganizations.getState().followTo(asked)
    if (step !== 'ask' || !id) return
    let current = true
    void orgOf(id).then(org => {
      if (current) setAnswer({ key, asked: org })
    })
    return () => {
      current = false
    }
  }, [step, key, id, asked, orgOf, navigate, to])
}

/**
 * `useCloseWhenMissing` for a page or popup whose path names a NODE (`/nodes/<id>/files`,
 * `/service/<node>`, `/node/permissions/<node>`): it follows the node to its organization, or goes
 * to the home view when the reader can read no such node. An empty or absent id does nothing.
 */
export function useCloseUnlessOrgNode(nodeId: string | undefined) {
  useCloseWhenMissing({
    id: nodeId || undefined,
    found: useNode(state => state.nodes.some(node => node.id === nodeId)),
    loadedOrgId: useNode(state => state.loadedOrgId),
    orgOf: useNode(state => state.orgOf),
    to: HOME,
  })
}

/** The same for a path that names a NETWORK (`/networks/<id>/files`, `/node/<network>`, `/compute/<network>`). */
export function useCloseUnlessOrgNetwork(networkId: string | undefined) {
  useCloseWhenMissing({
    id: networkId || undefined,
    found: useNetwork(state => state.networks.some(network => network.id === networkId)),
    loadedOrgId: useNetwork(state => state.loadedOrgId),
    orgOf: useNetwork(state => state.orgOf),
    to: HOME,
  })
}
